import React, { useEffect, useState } from 'react';
import { Mail, Phone, Globe, Trash2, Check, X, Loader2, FileText } from 'lucide-react';
import { adminApi } from '../../api';
import { PageHeader, Table, Modal, Field, Select, Textarea } from './AdminUI';

const STATUSES = [
  { key: 'new', label: 'Nuovo', color: 'bg-blue-100 text-blue-800' },
  { key: 'contacted', label: 'Contattato', color: 'bg-yellow-100 text-yellow-800' },
  { key: 'approved', label: 'Approvato', color: 'bg-green-100 text-green-800' },
  { key: 'rejected', label: 'Rifiutato', color: 'bg-gray-200 text-gray-600' }
];

const TIER_LABEL = {
  featured: 'In evidenza', top3: 'Top 3', recommended: 'Consigliato', custom: 'Su misura'
};

export default function AdminPartnerLeads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);

  const load = () => { setLoading(true); adminApi.partnerLeads().then(setItems).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const rows = filter ? items.filter(l => l.status === filter) : items;

  const setStatus = async (row, status) => {
    await adminApi.updatePartnerLead(row.id, { status });
    load();
    if (selected?.id === row.id) setSelected({ ...selected, status });
  };
  const saveNotes = async (row, notes) => {
    await adminApi.updatePartnerLead(row.id, { notes });
    load();
    if (selected?.id === row.id) setSelected({ ...selected, notes });
  };
  const del = async (row) => {
    if (!window.confirm(`Eliminare la richiesta di "${row.business_name}"?`)) return;
    await adminApi.deletePartnerLead(row.id); setSelected(null); load();
  };

  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s.key]: items.filter(i => i.status === s.key).length }), {});

  return (
    <div>
      <PageHeader
        title="Candidature Partner"
        subtitle={`${items.length} richieste ricevute da /promuoviti — attività che vogliono sponsorizzarsi`}
      />

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setFilter('')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold ${!filter ? 'bg-[color:var(--yup-primary)] text-black' : 'bg-gray-100 text-gray-600'}`}
                data-testid="filter-all">Tutte ({items.length})</button>
        {STATUSES.map(s => (
          <button key={s.key} onClick={() => setFilter(s.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === s.key ? 'bg-[color:var(--yup-primary)] text-black' : s.color}`}
                  data-testid={`filter-${s.key}`}>
            {s.label} ({counts[s.key] || 0})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500"><Loader2 className="inline animate-spin" size={20}/> Loading...</div>
      ) : (
        <Table
          columns={[
            { key: 'business_name', label: 'Attività', render: r => (
              <div>
                <div className="font-semibold text-gray-900">{r.business_name}</div>
                <div className="text-xs text-gray-500">{r.contact_name}</div>
              </div>
            )},
            { key: 'email', label: 'Contatti', render: r => (
              <div className="text-xs">
                <a href={`mailto:${r.email}`} className="text-blue-600 hover:underline flex items-center gap-1"><Mail size={11}/> {r.email}</a>
                {r.phone && <a href={`tel:${r.phone}`} className="text-gray-600 flex items-center gap-1 mt-0.5"><Phone size={11}/> {r.phone}</a>}
              </div>
            )},
            { key: 'city_slug', label: 'Città / Cat.', render: r => (
              <div className="text-xs">
                <div>{r.city_slug || '—'}</div>
                <div className="text-gray-400">{r.category || '—'}</div>
              </div>
            )},
            { key: 'tier', label: 'Piano', render: r => (
              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-900">{TIER_LABEL[r.tier] || r.tier}</span>
            )},
            { key: 'status', label: 'Stato', render: r => {
              const s = STATUSES.find(x => x.key === r.status) || STATUSES[0];
              return <span className={`px-2 py-1 rounded text-xs font-semibold ${s.color}`}>{s.label}</span>;
            }},
            { key: 'created_at', label: 'Ricevuta', render: r => r.created_at ? new Date(r.created_at).toLocaleDateString('it-IT') : '—' }
          ]}
          rows={rows}
          actions={row => (
            <div className="flex gap-1 justify-end">
              <button onClick={() => setSelected(row)} className="p-1.5 hover:bg-gray-100 rounded text-blue-600" title="Dettagli"><FileText size={15}/></button>
              {row.status !== 'approved' && (
                <button onClick={() => setStatus(row, 'approved')} className="p-1.5 hover:bg-green-100 rounded text-green-700" title="Approva"><Check size={15}/></button>
              )}
              {row.status !== 'rejected' && (
                <button onClick={() => setStatus(row, 'rejected')} className="p-1.5 hover:bg-gray-100 rounded text-gray-500" title="Rifiuta"><X size={15}/></button>
              )}
              <button onClick={() => del(row)} className="p-1.5 hover:bg-red-100 rounded text-red-600"><Trash2 size={15}/></button>
            </div>
          )}
        />
      )}

      {/* Detail modal */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.business_name} maxWidth="max-w-2xl">
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Info label="Referente" value={selected.contact_name}/>
              <Info label="Email" value={<a href={`mailto:${selected.email}`} className="text-blue-600">{selected.email}</a>}/>
              <Info label="Telefono" value={selected.phone ? <a href={`tel:${selected.phone}`}>{selected.phone}</a> : '—'}/>
              <Info label="Sito" value={selected.website ? <a href={selected.website} target="_blank" rel="noreferrer" className="text-blue-600">{selected.website}</a> : '—'}/>
              <Info label="Città" value={selected.city_slug || '—'}/>
              <Info label="Categoria" value={selected.category || '—'}/>
              <Info label="Piano interesse" value={TIER_LABEL[selected.tier] || selected.tier}/>
              <Info label="Ricevuta il" value={selected.created_at ? new Date(selected.created_at).toLocaleString('it-IT') : '—'}/>
            </div>
            {selected.message && (
              <div>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">Messaggio</div>
                <div className="p-3 rounded bg-gray-50 text-sm text-gray-800 whitespace-pre-wrap">{selected.message}</div>
              </div>
            )}
            <Field label="Cambia stato">
              <Select value={selected.status} onChange={e => setStatus(selected, e.target.value)}>
                {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </Select>
            </Field>
            <Field label="Note interne">
              <Textarea rows={3} defaultValue={selected.notes || ''} onBlur={e => saveNotes(selected, e.target.value)} placeholder="Trattativa, referente incaricato, ecc."/>
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold mb-1">{label}</div>
      <div className="text-gray-900">{value}</div>
    </div>
  );
}
